/***
*
*   submitFailureOutcome
*   What the public form page does when its submit POST fails.
*
*   Returns { reopen, message, fieldLines }. reopen is true when the page
*   must put the survey back on its last page with the answers kept; false
*   on a 5xx, because the server has usually stored the response already
*   and a second submit stores a second copy.
*
*   Only a 400 with a JSON body shows server text: those are deliberate
*   refusals (a bad TFN, a disabled entity type). Every other failure gets
*   a fixed text: a 5xx can carry raw server error text, a 429 answer is a
*   plain string, and a 503 can be an HTML page.
*
*   fieldLines turn the 400 error labels (key paths such as
*   "entities / #2 / entity_tfn", never TFN digits) into words from the
*   served schema, for example "Entity 2 - Entity Tax File Number (TFN)".
*
*   Pure, like upload-error.js, so it is testable without mounting a view.
*
**********/

export const SUBMIT_SENT_TEXT =
  "Thank you, your answers were sent. Something went wrong on our side while we processed them. " +
  "Please do not submit the form again. The firm may contact you if they need anything more.";

export const SUBMIT_GENERIC_TEXT =
  "Sorry, we could not submit your form. Your answers are still here. Please check them and try again.";

export const SUBMIT_NETWORK_TEXT =
  "We could not reach our server, so your form was not submitted. Your answers are still here. " +
  "Please check your internet connection and try again.";

export const SUBMIT_RATE_LIMIT_TEXT =
  "There have been too many attempts in a short time. Your answers are still here. " +
  "Please wait a minute, then try again.";

export const SUBMIT_LINK_INACTIVE_TEXT =
  "This form link is no longer active, so your form was not submitted. " +
  "Please contact the firm that sent you the link.";

const PANEL_INDEX = /\{\s*panelIndex\s*\}/gi;
const PLACEHOLDER = /\{[^}]*\}/;

export function submitFailureOutcome(err, schema) {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    // axios sets request but no response when the server never answered.
    const networkError = !!err && typeof err === "object" && !!err.request;
    return reopenWith(networkError ? SUBMIT_NETWORK_TEXT : SUBMIT_GENERIC_TEXT);
  }

  if (status >= 500 && status <= 599) {
    return { reopen: false, message: SUBMIT_SENT_TEXT, fieldLines: [] };
  }
  if (status === 429) return reopenWith(SUBMIT_RATE_LIMIT_TEXT);
  if (status === 403 || status === 404) return reopenWith(SUBMIT_LINK_INACTIVE_TEXT);

  const data = err.response.data;
  if (status !== 400 || !isPlainObject(data)) return reopenWith(SUBMIT_GENERIC_TEXT);

  const message = typeof data.message === "string" && data.message !== "" ? data.message : SUBMIT_GENERIC_TEXT;
  const errors = Array.isArray(data.errors) ? data.errors : [];
  const fieldLines = errors
    .filter((error) => isPlainObject(error) && typeof error.label === "string" && error.label !== "")
    .map((error) => {
      const field = friendlyFieldLabel(error.label, schema);
      return typeof error.message === "string" && error.message !== "" ? `${field}: ${error.message}` : field;
    });

  return { reopen: true, message, fieldLines };
}

/*
 * friendlyFieldLabel
 * Walks a " / " key path through the schema. Plain panels do not nest data,
 * so a key is looked up through them; a "#N" segment follows a dynamic panel
 * and becomes its templateTitle with N. Any segment that does not resolve
 * gives back the raw label.
 */
export function friendlyFieldLabel(label, schema) {
  if (typeof label !== "string") return "";

  const segments = label.split(" / ");
  const parts = [];
  let scope = topLevelElements(schema);
  let dynamicPanel = null;

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const index = /^#(\d+)$/.exec(segment);

    if (index) {
      if (!dynamicPanel) return label;
      parts.push(panelTitle(dynamicPanel, index[1]));
      scope = asList(dynamicPanel.templateElements);
      dynamicPanel = null;
      continue;
    }

    if (dynamicPanel) return label;
    const element = findByName(scope, segment);
    if (!element) return label;

    if (i === segments.length - 1) {
      parts.push(titleOf(element));
    } else if (element.type === "paneldynamic") {
      dynamicPanel = element;
    } else {
      return label;
    }
  }

  return parts.join(" - ");
}

function reopenWith(message) {
  return { reopen: true, message, fieldLines: [] };
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function asList(value) {
  return Array.isArray(value) ? value : [];
}

function topLevelElements(schema) {
  if (!isPlainObject(schema)) return [];
  const pages = asList(schema.pages).flatMap((page) =>
    isPlainObject(page) ? [...asList(page.elements), ...asList(page.questions)] : []
  );
  return [...pages, ...asList(schema.elements), ...asList(schema.questions)];
}

function findByName(elements, name) {
  for (const element of elements) {
    if (!isPlainObject(element)) continue;
    if (element.name === name) return element;
    if (element.type === "paneldynamic") continue;
    const found = findByName([...asList(element.elements), ...asList(element.questions)], name);
    if (found) return found;
  }
  return null;
}

// A SurveyJS text is a string or a locale map such as { default, fr }.
function localText(value) {
  if (typeof value === "string") return value;
  if (isPlainObject(value)) {
    if (typeof value.default === "string") return value.default;
    const first = Object.values(value).find((text) => typeof text === "string");
    if (first) return first;
  }
  return "";
}

function titleOf(element) {
  return localText(element.title).trim() || element.name;
}

function panelTitle(dynamicPanel, number) {
  const template = localText(dynamicPanel.templateTitle).trim();
  const filled = template.replace(PANEL_INDEX, number);
  if (filled !== template && !PLACEHOLDER.test(filled)) return filled;
  const base = template && !PLACEHOLDER.test(template) ? template : titleOf(dynamicPanel);
  return `${base} #${number}`;
}
